import React, { useRef } from 'react';
import {
  Box,
  Select,
  MenuItem,
  IconButton,
  FormControl,
  Container,
  TextField,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useFormikContext, FieldArray, Field, Form } from 'formik';
import FileUpload from 'Components/FileUpload';
import CssButton from 'Components/CssButton';

const purple = "#5a407a";
const purpleLight = "#6d4e9c";
const purpleBorder = "#a084c7";

const assetTypeOptions = [
  { value: "PDF", label: "PDF" },
  { value: "IMAGE", label: "Image" },
  { value: "VIDEO", label: "Video" },
];

const defaultAsset = {
  label: "Script 1",
  type: "PDF",
  file: null,
  saveImage: false,
  handleUpdatedFile: false,
};

const getAccept = (type) => {
  switch (type) {
    case 'PDF':
      return { 'application/pdf': ['.pdf'] };
    case 'IMAGE':
      return { 'image/*': ['.jpeg', '.png', '.jpg', '.gif'] };
    case 'VIDEO':
      return { 'video/*': ['.mp4'] };
    default:
      return {};
  }
};

const AssetsForm = () => {
  const { values, setFieldValue, submitForm } = useFormikContext();
  const childFuncs = useRef(null);

  return (
    <Form style={{ width: '100%' }}>
      <Container sx={{ mt: 2 }}>
        <FieldArray name="scriptsAsset">
          {({ push, remove }) => (
            <Stack spacing={3} sx={{ gap: '20px' }}>
              {(values.scriptsAsset || []).map((asset, idx) => (
                <Box
                  key={idx}
                  sx={{
                    borderRadius: 1,
                    p: 2,
                    color: '#fff',
                    minWidth: 400,
                    width: '100%',
                    maxWidth: 600,
                    m: '0 auto !important',
                    border: '1px solid #fff',
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Field
                      as={TextField}
                      name={`scriptsAsset[${idx}].label`}
                      value={asset.label}
                      onChange={e => setFieldValue(`scriptsAsset[${idx}].label`, e.target.value)}
                      InputProps={{
                        disableUnderline: false,
                        style: { color: '#fff', fontWeight: 400, fontSize: 16 },
                      }}
                      sx={{
                        input: { color: '#fff', padding: '9px 14px' },
                        label: { color: '#fff' },
                        width: 180,
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Field
                          as={Select}
                          name={`scriptsAsset[${idx}].type`}
                          value={asset.type}
                          onChange={e => setFieldValue(`scriptsAsset[${idx}].type`, e.target.value)}
                          sx={{
                            color: '#fff',
                            borderColor: purpleBorder,
                            '.MuiOutlinedInput-notchedOutline': {
                              borderColor: purpleBorder,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: purpleBorder,
                            },
                          }}
                        >
                          {assetTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                      <IconButton onClick={() => remove(idx)} sx={{ color: '#fff' }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  {/* FileUpload Area */}
                  <div className='form_control'>
                    <FileUpload
                      name={`scriptsAsset[${idx}].file`}
                      id={`asset-file-${idx}`}
                      multiple={false}
                      selectedFile={asset.file}
                      onFileUpload={({ url }) => {
                        if (url) {
                          let fileUrl = url?.substring(0, url?.indexOf("?"));
                          var path = fileUrl.split("/");
                          fileUrl = process.env.REACT_APP_CDN_URL
                            ? `${process.env.REACT_APP_CDN_URL}/${path[3]}/${path[4]}`
                            : fileUrl;
                          setFieldValue(`scriptsAsset[${idx}].file`, fileUrl);
                        } else {
                          setFieldValue(`scriptsAsset[${idx}].file`, "");
                        }
                      }}
                      saveImage={value => setFieldValue(`scriptsAsset[${idx}].saveImage`, value)}
                      handleUpdatedFile={value => setFieldValue(`scriptsAsset[${idx}].handleUpdatedFile`, value)}
                      simulationForm={true}
                      accept={getAccept(asset.type)}
                      bucket="other"
                      title={`Upload ${asset.type}`}
                      saveOnDisable={() => {}}
                      handleSpinner={() => {}}
                      childFunc={childFuncs}
                    />
                  </div>
                </Box>
              ))}
              <CssButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => push({ ...defaultAsset, label: `Script ${values.scriptsAsset.length + 1}` })}
                sx={{
                  background: purple,
                  color: '#fff',
                  width: 'fit-content',
                  alignSelf: 'center',
                  '&:hover': { background: purpleLight },
                }}
              >
                Add Asset
              </CssButton>
              <CssButton
                type="button"
                onClick={submitForm}
                variant="contained"
                sx={{
                  background: purpleLight,
                  color: '#fff',
                  width: 'fit-content',
                  alignSelf: 'center',
                }}
              >
                Submit
              </CssButton>
            </Stack>
          )}
        </FieldArray>
      </Container>
    </Form>
  );
};

export default AssetsForm;